import { formatDecimal, tt } from '@agos/shared'

/**
 * A partner's share, and the sum nothing was checking - `SAAS.md` §3.9.
 *
 * **FB-505:** *"a partner's share decides a payout and cannot be set."* Four write endpoints with no
 * caller, and no admin screen at all - only the partner's own portal.
 *
 * **The finding is not the missing screen, it is the arithmetic.** `PartnersAdminService` bounds each
 * share individually, `0 <= sharePct <= 100`, and **nothing anywhere checks the total across a
 * franchise**. Measured on the owner's central database: three franchises, two partners each, shares
 * summing to exactly **100,00** on all three.
 *
 * **That total is a coincidence of their data, not an invariant - corrected 2026-09-17 by the owner.**
 * Asked whether the server should demand 100, they said: *"Share is not always 100%, because some of
 * them have for example 10% in our firm, and we dont need that as a share."* A partner holds 10 %;
 * the other 90 % is the firm's own and is **not a share waiting to be allocated**. So nothing here
 * demands 100, and nothing should: a total below it is the ordinary case, which is why
 * {@link allocationNote} reads *"X % allocated, Y % stays with the firm"* rather than complaining.
 * **Only a total above 100 is wrong**, because 110 % of a profit does not exist - and that is the
 * one the screen refuses.
 *
 * **And the clamp is what makes an overrun invisible.** `ProfitabilityService` pays each partner
 * `profit * share / 100` independently and then reports
 * `housePct = Math.max(0d, 100d - sharedTotalPct)`. At 150 % every partner is quoted their full
 * payout, the house reads **0 %** rather than −50 %, and nothing says the splits add to more than
 * the profit being split.
 */

/** The most a franchise's shares may add to. The house takes whatever is left. */
export const FULL_SHARE = 100

export interface Partner {
  id: number
  name: string
  email: string | null
  iban: string | null
  /**
   * Four fields the list has always answered and this type did not read - §3.106.
   *
   * `GET /super-admin/partners` selects all 14 columns and `PartnersAdminService.update` patches
   * nine, so these were **answered and never read** rather than parsed and undrawn. Measured on the
   * owner's two partners: both have a `phone` and both have `notes`, neither reachable from any
   * screen. `ico` and `dic` are empty on both, and §3.34 is a whole entry about validating them -
   * on fields no user interface has ever been able to send.
   */
  phone: string | null
  ico: string | null
  dic: string | null
  notes: string | null
  defaultSharePct: number
  active: boolean
  /**
   * The address they sign into the portal with, or null when they cannot sign in at all.
   *
   * **`login_email`, which is a different field from `email`.** The list answers both: `email` is
   * where their invoices go, `login_email` is their portal account - and `PUT …/{id}/login` is what
   * grants or withdraws it. A partner can be invoiced without ever being given a login, which is
   * what both of the owner's two partners are.
   *
   * The password hash is on the same row and is deliberately **not** read: a screen needs to know
   * whether somebody can sign in, never what with.
   */
  loginEmail: string | null
  /** When they last did, or null - the honest way to say *granted but never used*. */
  lastLoginAt: string | null
  /**
   * Whether a password exists to sign in **with** - §3.125.
   *
   * **The comment above is right about the hash and this is the field it was asking for.**
   * `PartnersAdminService.masked` strips `passwordHash` and replaces it with exactly this boolean, so
   * *whether somebody can sign in* is answered without *what with* - and nothing read it. The screen
   * inferred a portal account from `loginEmail` alone, and `PUT …/{id}/login` writes the hash only when
   * a password is sent, so an address can exist without one. §10A.238 stops new ones; this reads the
   * rows that are already there.
   */
  passwordSet: boolean
}

export interface FranchiseShare {
  /**
   * The **link row's** id, not the partner's - `franchise_partners.id`.
   *
   * Read because `DELETE …/franchise-partners/{id}` takes it and nothing else identifies the row:
   * removing a partner's share in one franchise is a different request from removing the partner,
   * and it is addressed by the link. Null only for a payload that answered without it.
   */
  linkId: number | null
  partnerId: number
  partnerName: string
  sharePct: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function partnersOf(payload: unknown): Partner[] {
  const wrapped = (payload as { partners?: unknown } | undefined)?.partners
  const rows = Array.isArray(payload) ? payload : Array.isArray(wrapped) ? wrapped : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name) ?? `#${row.id}`,
        email: text(row.email),
        iban: text(row.iban),
        // §3.106 - answered by the list all along and read by nothing.
        phone: text(row.phone),
        ico: text(row.ico),
        dic: text(row.dic),
        notes: text(row.notes),
        defaultSharePct: count(row.defaultSharePct),
        // Absent is active, matching the column's default and every other reader on this product.
        active: row.active !== false,
        loginEmail: text(row.loginEmail),
        /* §3.125 - **false when absent, which is the safe direction.** Claiming an account works when
           it does not is the defect this entry is about; the other way round only costs a *Dať
           prihlásenie* button on a partner who already has one, and the server refuses a pointless
           grant anyway. */
        passwordSet: row.passwordSet === true,
        lastLoginAt: text(row.lastLoginAt),
      },
    ]
  })
}

/**
 * A franchise's shares, out of `GET …/franchises/{id}/partners`.
 *
 * **The key is `links`, not `partners`** - read off the controller rather than guessed, because the
 * partner list next door answers under `partners` and the two are one letter apart in a screen that
 * calls both. The bare array is accepted too, which is what a test double hands over.
 */
export function sharesOf(payload: unknown): FranchiseShare[] {
  const wrapped = (payload as { links?: unknown; partners?: unknown } | undefined) ?? undefined
  const keyed = Array.isArray(wrapped?.links)
    ? wrapped.links
    : Array.isArray(wrapped?.partners)
      ? wrapped.partners
      : []
  const rows = Array.isArray(payload) ? payload : keyed
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const id = typeof row.partnerId === 'number' ? row.partnerId : null
    if (id === null) return []
    return [
      {
        linkId: typeof row.id === 'number' ? row.id : null,
        partnerId: id,
        partnerName: text(row.partnerName) ?? text(row.name) ?? `#${id}`,
        sharePct: count(row.sharePct),
      },
    ]
  })
}

/**
 * What this franchise's partners hold between them.
 *
 * **The figure the clamp was hiding.** `ProfitabilityService` reports the house as
 * `max(0, 100 - total)`, so a total of 150 reads as a house share of nought rather than as an
 * over-allocation - and every partner is still quoted their full payout.
 */
export function allocatedPct(shares: readonly FranchiseShare[]): number {
  // Rounded to two places, because the shares are `numeric(x,2)` and summing them in floating point
  // otherwise produces 99.99999999999999 for three thirds and refuses a save that is exactly right.
  return Math.round(shares.reduce((sum, share) => sum + share.sharePct, 0) * 100) / 100
}

/** What the house keeps - and **negative when over-allocated**, which is the whole point. */
export function housePct(shares: readonly FranchiseShare[]): number {
  return Math.round((FULL_SHARE - allocatedPct(shares)) * 100) / 100
}

/**
 * What the allocation would be with this one partner's share changed to `next`.
 *
 * A change rather than an addition, because the endpoint upserts: `POST …/{franchiseId}/partners`
 * with a partner already linked *restates* their share, so what a save produces is the total with
 * their old value replaced, not added to.
 */
export function allocatedWith(
  shares: readonly FranchiseShare[],
  partnerId: number,
  next: number,
): number {
  const others = shares.filter((share) => share.partnerId !== partnerId)
  return Math.round((others.reduce((sum, share) => sum + share.sharePct, 0) + next) * 100) / 100
}

export type ShareProblem = 'not-a-number' | 'negative' | 'over-100' | 'over-allocated' | null

/**
 * Why this share cannot be saved, or null when it can.
 *
 * **`over-allocated` is the one the server cannot answer.** Its own check is per row, so 50 % added
 * to a franchise already at 100 % is a valid request by every rule it has - and the result is
 * payouts that cannot all be made.
 */
export function shareProblem(
  raw: string,
  shares: readonly FranchiseShare[],
  partnerId: number,
): ShareProblem {
  const value = Number(raw.trim().replace(',', '.'))
  if (raw.trim() === '' || !Number.isFinite(value)) return 'not-a-number'
  if (value < 0) return 'negative'
  if (value > FULL_SHARE) return 'over-100'
  return allocatedWith(shares, partnerId, value) > FULL_SHARE ? 'over-allocated' : null
}

/** What to say, and for the over-allocation it says by how much. */
export function shareProblemMessage(
  problem: ShareProblem,
  shares: readonly FranchiseShare[],
  partnerId: number,
  raw: string,
): string {
  if (problem === null) return ''
  if (problem === 'not-a-number') return tt('Podiel musí byť číslo')
  if (problem === 'negative') return tt('Podiel nemôže byť záporný')
  if (problem === 'over-100') return tt('Podiel nemôže byť viac ako 100 %')
  const total = allocatedWith(shares, partnerId, Number(raw.trim().replace(',', '.')))
  return `${tt('Podiely by dohromady dali')} ${formatDecimal(total, 2)} %, ${tt('čo je o')} ${formatDecimal(total - FULL_SHARE, 2)} ${tt('% viac, než sa dá rozdeliť. Najprv zniž iný podiel.')}`
}

/**
 * What the franchise's allocation reads as, over the list.
 *
 * Says the house's share explicitly, because *the rest goes to the house* is the fact
 * `ProfitabilityService`'s clamp was obscuring - and an over-allocation is named rather than
 * clamped to nought.
 */
export function allocationNote(shares: readonly FranchiseShare[]): string {
  const allocated = allocatedPct(shares)
  const house = housePct(shares)
  if (house < 0) {
    return `${tt('Rozdelených')} ${formatDecimal(allocated, 2)} % - ${tt('o')} ${formatDecimal(-house, 2)} ${tt('% viac, než existuje. Partnerom by sa vyplatilo viac, než je zisk.')}`
  }
  if (allocated === 0) {
    return tt('Žiadny podiel nie je rozdelený - celý zisk zostáva firme.')
  }
  return `${tt('Rozdelených')} ${formatDecimal(allocated, 2)} %, ${tt('firme zostáva')} ${formatDecimal(house, 2)} %.`
}

/** True when the splits already exceed the profit, which is a state the screen must not hide. */
export function isOverAllocated(shares: readonly FranchiseShare[]): boolean {
  return housePct(shares) < 0
}

/**
 * Whether this partner may be removed.
 *
 * **`DELETE …/partners/{id}` does not check, and the share rows cascade away with them.**
 * `franchise_partners.partner_id` is `ON DELETE CASCADE` (`V1__baseline.sql:1104`), so there is no
 * orphan link - which is worse rather than better: deleting a partner who holds 40 % of a franchise
 * moves 40 % of its profit to the house silently, with no row left to say what the share had been.
 * So the screen refuses while they hold one, and says how many.
 */
export function mayDelete(partnerId: number, allShares: readonly FranchiseShare[]): boolean {
  return !allShares.some((share) => share.partnerId === partnerId)
}

export function deleteRefusal(partnerId: number, allShares: readonly FranchiseShare[]): string {
  const held = allShares.filter((share) => share.partnerId === partnerId).length
  if (held === 0) return ''
  return `${tt('Tento partner má podiel v')} ${held} ${tt('prevádzkach. Najprv mu podiely odober - zmazaním partnera by podiely zmizli bez záznamu a zisk by potichu prešiel firme.')}`
}

/** The fields `PATCH …/partners/{id}` will take from this screen - all nine since §3.106. */
export interface PartnerDraft {
  name: string
  email: string
  iban: string
  phone: string
  ico: string
  dic: string
  notes: string
  /** Raw text, because `Number('30,00')` is `NaN` and a Slovak keyboard types a comma. */
  sharePct: string
  active: boolean
}

export function draftOf(partner: Partner): PartnerDraft {
  return {
    name: partner.name,
    email: partner.email ?? '',
    iban: partner.iban ?? '',
    phone: partner.phone ?? '',
    ico: partner.ico ?? '',
    dic: partner.dic ?? '',
    notes: partner.notes ?? '',
    sharePct: formatDecimal(partner.defaultSharePct, 2),
    active: partner.active,
  }
}

/** What a share box says when the server would refuse it - the bound, not *Invalid*. */
export function sharePctProblem(raw: string): string {
  const text = raw.trim().replace(',', '.')
  if (text === '') return ''
  const value = Number(text)
  if (!Number.isFinite(value)) return tt('Podiel musí byť číslo.')
  // `validate` refuses below 0 and above 100, so the screen refuses the same two and says which.
  if (value < 0) return tt('Podiel nemôže byť mínusový.')
  if (value > FULL_SHARE) return `${tt('Podiel nemôže byť viac než')} ${FULL_SHARE} %.`
  return ''
}

/**
 * Only the fields that changed - the §3.5 diff, for the same reason.
 *
 * `PartnersAdminService.update` builds its `SET` clause from the keys present, so a whole-row send
 * is a write to every column including the ones nobody touched. And `null` is a value it stores:
 * clearing an address must send `null`, while *not changing* it must send nothing at all, and a
 * draft built from empty strings cannot tell those apart unless the comparison is against what was
 * read.
 *
 * `iban` is why this is here rather than deferred: it is where a payout goes, and a screen that can
 * add a partner but never correct their account number is one typo from a misdirected payment.
 */
export function partnerPatch(
  partner: Partner,
  draft: PartnerDraft,
): Record<string, string | number | boolean | null> {
  const patch: Record<string, string | number | boolean | null> = {}
  if (draft.name.trim() !== partner.name) patch.name = draft.name.trim()
  // §3.106 - the five text fields all follow one rule, which is the rule this function already had
  // for two of them: *blank means null, unchanged means absent*. Listing them rather than looping
  // over the draft, because `sharePct` and `active` are not strings and `name` is never nullable.
  const texts: [keyof PartnerDraft, string | null][] = [
    ['email', partner.email],
    ['iban', partner.iban],
    ['phone', partner.phone],
    ['ico', partner.ico],
    ['dic', partner.dic],
    ['notes', partner.notes],
  ]
  for (const [field, stored] of texts) {
    const typed = String(draft[field]).trim()
    if (typed !== (stored ?? '')) patch[field] = typed === '' ? null : typed
  }
  // **A blank share box sends nothing rather than 0.** The column is `NOT NULL DEFAULT 0` and 0 %
  // is a real value somebody might mean, so an empty box cannot stand for it: clearing the text is
  // *I have not decided*, and the stored figure stays.
  const share = draft.sharePct.trim().replace(',', '.')
  if (share !== '' && sharePctProblem(draft.sharePct) === '') {
    const value = Number(share)
    if (value !== partner.defaultSharePct) patch.defaultSharePct = value
  }
  if (draft.active !== partner.active) patch.active = draft.active
  return patch
}

/** The fields the create form takes beyond the name and the e-mail - §3.106. */
export interface NewPartnerRest {
  phone: string
  ico: string
  dic: string
  notes: string
  iban: string
}

export function emptyNewRest(): NewPartnerRest {
  return { phone: '', ico: '', dic: '', notes: '', iban: '' }
}

/**
 * Only what was typed - §3.106.
 *
 * **Every one of these columns is defaulted or nullable on the server**, so a blank box must send
 * nothing rather than an empty string: `''` in `ico` is not *not known*, it is a stored empty
 * identifier, and §3.34's validators exempt blank precisely so that absence stays absence.
 *
 * **`defaultSharePct` is deliberately not among them, and that is §3.9's decision rather than an
 * oversight.** Its e2e case states the reason - *"a default one set here is the field that lets
 * somebody believe they have allocated something they have not"* - so a new partner takes the
 * column's `DEFAULT 0` and the figure is corrected on the **edit** form, where the question is
 * different: it is read by `PartnerPortalService` and shown to that partner, and until §3.106 it
 * could not be corrected anywhere.
 */
export function newPartnerRest(draft: NewPartnerRest): Record<string, string> {
  const body: Record<string, string> = {}
  for (const field of ['phone', 'ico', 'dic', 'notes', 'iban'] as const) {
    const typed = draft[field].trim()
    if (typed !== '') body[field] = typed
  }
  return body
}

/** Nothing changed, so there is nothing to send - the server answers *Žiadne zmeny* to an empty one. */
export function mayPatch(partner: Partner, draft: PartnerDraft): boolean {
  // The name is the one field `validate` refuses empty, and it refuses it on the patch too.
  if (draft.name.trim() === '') return false
  // §3.106 - and a share the server would refuse is refused here, so the save is not offered against
  // a figure that comes back *Invalid* with nothing said about which field.
  if (sharePctProblem(draft.sharePct) !== '') return false
  return Object.keys(partnerPatch(partner, draft)).length > 0
}

/**
 * What granting or withdrawing a portal sign-in does, said before it is done.
 *
 * The same shape §3.7 uses for a franchise administrator, and for the same reason: it takes effect
 * on sessions that are already open, and a confirmation that only asked *are you sure* would
 * withhold the one fact somebody needs.
 */
export function loginNote(granting: boolean): string {
  return granting
    ? tt('Partner sa bude môcť prihlásiť do portálu a uvidí svoje prevádzky, faktúry a podiely.')
    : tt('Prihlásenie sa odoberie okamžite, aj na už otvorené sedenia. Podiely zostávajú.')
}

/**
 * Whether this partner can sign into the portal at all - §3.125.
 *
 * **Both halves, because an address is not an account.** This read `loginEmail !== null`, and the server
 * writes the hash only when a password is sent - so a partner invited by e-mail and never given one
 * showed as having a login and could not use it. Legacy's route has the same hole, which is why such a
 * row may be in the restored data rather than only in theory.
 */
export function hasLogin(partner: Partner): boolean {
  return partner.loginEmail !== null && partner.passwordSet
}

/**
 * The three states a portal account can be in - §3.125.
 *
 * `usable` they can sign in, `invited` there is an address and no password, `none` neither. The middle
 * one is the whole point of this entry and had no words; it is named rather than merged into either
 * neighbour, because *invite them* and *give them a password* are different actions.
 */
export type PortalAccount = 'usable' | 'invited' | 'none'

export function portalAccountOf(partner: Partner): PortalAccount {
  if (partner.loginEmail === null) return 'none'
  return partner.passwordSet ? 'usable' : 'invited'
}

/**
 * What to say about a sign-in that exists and has never been used.
 *
 * Worth saying because it is the state a granted-and-forgotten login sits in, and it is
 * indistinguishable from *no login* on any screen that only shows a tick.
 */
export function loginState(partner: Partner): string {
  if (partner.loginEmail === null) return tt('Bez prihlásenia')
  /* §3.125 - **said before *never signed in*, because it is the stronger fact.** A partner with no
     password has not merely failed to use their account; they cannot. Saying *nikdy sa neprihlásil* of
     them would be true and would send somebody chasing a partner who was never able to. */
  if (!partner.passwordSet) return `${partner.loginEmail} - ${tt('bez hesla, nemôže sa prihlásiť')}`
  return partner.lastLoginAt === null
    ? `${partner.loginEmail} - ${tt('nikdy sa neprihlásil')}`
    : partner.loginEmail
}

/**
 * The server's own floor, stated before the request rather than after it.
 *
 * **Eight, by owner decision #18 - `SAAS.md §3.71`.** This read six, matching
 * `PartnersAdminService.MIN_PASSWORD`, and the reason it matched still stands: a client that refuses
 * what the server accepts makes a rule nobody can find. What was asked and answered is which number
 * the two should agree on. *"Make it longer - eight or more"*, the argument being that a partner's
 * portal sits on the public internet and shows revenue, costs, profit and payouts across every
 * franchise they hold a share of.
 *
 * **The staff floor is not swept with it.** `new-franchise.ts` validates a franchise
 * administrator's password at six and `StaffAuthService` agrees: the same number for a different
 * reason, and the answer was about the portal.
 */
export const MIN_PASSWORD = 8

export function partnerPasswordProblem(password: string): string {
  if (password.length === 0) return tt('Heslo je povinné')
  if (password.length < MIN_PASSWORD) {
    return `${tt('Heslo musí mať aspoň')} ${MIN_PASSWORD} ${tt('znakov')}`
  }
  return ''
}

/**
 * Whether the screen knows which franchise's shares it is talking about - `SAAS.md` §3.176, FB-942.
 *
 * **§3.156 guarded the share read and this is the route its guard cannot see.** `links` carries
 * `enabled: franchiseId !== null`, and a query that never ran reports `isError` **false** - so a failed
 * **franchise** read left `sharesFailed` false while `allocationNote([])` printed *Žiadny podiel nie je
 * rozdelený* on the screen that manages what partners are paid. *Absent is not false*, in its query-state
 * form: every earlier instance of that rule in this repository was about a payload.
 *
 * **Four states rather than a boolean, because the figure is withheld for three different reasons and the
 * operator's next action differs for each.** Exhaustive in `sharesNote`, so a fifth is a compile error
 * rather than a screen with nothing to say (§9.507's guarantee).
 */
export type SharesState = 'known' | 'shares-unreadable' | 'franchises-unreadable' | 'no-franchises'

export function sharesState(input: {
  sharesFailed: boolean
  franchisesFailed: boolean
  franchiseCount: number
  franchiseId: number | null
}): SharesState {
  // The share read's own failure first: it is the narrower fact, and it is the one that stands even when a
  // franchise is selected and the list behind the picker is fine.
  if (input.sharesFailed) return 'shares-unreadable'
  if (input.franchiseId !== null) return 'known'
  // No franchise to ask about, and which of the two it is decides what the operator can do next.
  if (input.franchisesFailed) return 'franchises-unreadable'
  return input.franchiseCount === 0 ? 'no-franchises' : 'known'
}

/** True whenever the figure must be withheld, whatever the reason. */
export function sharesAreUnknown(state: SharesState): boolean {
  return state !== 'known'
}

/**
 * What to say instead of the figure.
 *
 * **The share read keeps its exact wording** - it is still true of the state it names, and a string that
 * changes when nothing has changed is how a translation drifts (§10A.277's rule).
 */
export function sharesNote(state: SharesState): string {
  switch (state) {
    case 'known':
      return ''
    case 'shares-unreadable':
      return tt(
        'Podiely sa nepodarilo načítať. Neznamená to, že žiadne nie sú - a preto sa teraz nedajú ani meniť.',
      )
    case 'franchises-unreadable':
      return tt(
        'Zoznam prevádzok sa nepodarilo načítať, takže nie je k čomu podiely vztiahnuť. Neznamená to, že žiadne nie sú.',
      )
    case 'no-franchises':
      return tt('Zatiaľ nie je žiadna prevádzka, takže niet čo rozdeľovať.')
  }
}
